// Completion, acceptance, follow-up, cancellation and reviews for a work record (website).
export const reviewMinLength = 10;
export const reviewMaxLength = 2000;
export const noteMaxLength = 2000;
export const maxEvidenceFiles = 10;

const statusLabels = {
  awarded: 'Awarded', in_progress: 'In progress', completion_submitted: 'Waiting for customer',
  completed: 'Completed', disputed: 'Problem reported', cancelled: 'Cancelled',
};
export const workStatusLabel = (status) => statusLabels[status] || 'Awarded';

/** "4.5 (12 reviews)", or null when there is nothing to show (never "0 stars"). */
export function ratingSummary(rating) {
  if (!rating || !rating.count || rating.average === null || rating.average === undefined) return null;
  return `${Number(rating.average).toFixed(1)} (${rating.count} ${rating.count === 1 ? 'review' : 'reviews'})`;
}

const messages = [
  ['WORK_BLOCKED', 'This work is paused because one of you has blocked the other. Contact Resident Service 360 support for help.'],
  ['WORK_WRONG_SIDE', 'The other side needs to do this.'],
  ['WORK_TRANSITION_INVALID', 'This step is no longer available. Reload the page.'],
  ['WORK_REASON_REQUIRED', 'Please give a reason.'],
  ['WORK_NOTE_TOO_LONG', `Please keep this under ${noteMaxLength} characters.`],
  ['COMPLETION_ATTACHMENT_INVALID', 'One of the files is not ready or belongs to other work. Remove it and try again.'],
  ['COMPLETION_TOO_MANY_FILES', `Add at most ${maxEvidenceFiles} files.`],
  ['CANCEL_ALREADY_REQUESTED', 'You have already asked to cancel. The other side needs to agree.'],
  ['VISIT_NOT_COMPLETABLE', 'Only a confirmed visit that has already happened can be marked as done.'],
  ['REVIEW_SELF_FORBIDDEN', 'You cannot review a company you belong to.'],
  ['REVIEW_FORBIDDEN', 'Only the customer can review this work.'],
  ['REVIEW_BLOCKED', 'Reviews are not available while one of you has blocked the other.'],
  ['REVIEW_NOT_AVAILABLE', 'You can review once you have accepted the finished work.'],
  ['REVIEW_RATING_INVALID', 'Choose a rating from 1 to 5 stars.'],
  ['REVIEW_TEXT_LENGTH', `Write between ${reviewMinLength} and ${reviewMaxLength} characters.`],
  ['REVIEW_ALREADY_EXISTS', 'This work has already been reviewed.'],
  ['REVIEW_EDIT_CLOSED', 'This review can no longer be changed.'],
  ['REVIEW_HIDDEN', 'This review has been hidden by Resident Service 360.'],
  ['RESPONSE_FORBIDDEN', 'Only company owners and admins can respond to reviews.'],
  ['RESPONSE_HIDDEN', 'Your response has been hidden by Resident Service 360.'],
  ['RESPONSE_TEXT_LENGTH', `Write between ${reviewMinLength} and ${reviewMaxLength} characters.`],
  ['POLICY_ACCEPTANCE_REQUIRED', 'Please accept the latest terms in your account before posting.'],
  ['ACCOUNT_SUSPENDED', 'Your account is suspended, so you cannot do this right now.'],
  ['WORK_NOT_FOUND_OR_FORBIDDEN', 'This work is not available to this account.'],
  ['SIGN_IN_REQUIRED', 'Sign in to continue.'],
];

function check(error) {
  if (!error) return;
  const text = `${error.message || ''} ${error.hint || ''}`;
  const match = messages.find(([code]) => text.includes(code));
  throw new Error(match ? match[1] : error.message);
}

async function call(client, name, args) {
  if (!client) throw new Error('Work updates are not connected. Please try again later.');
  const { data, error } = await client.rpc(name, args);
  check(error);
  return data;
}

function reason(text) {
  const trimmed = String(text || '').trim();
  if (!trimmed) throw new Error('Please give a reason.');
  if (trimmed.length > noteMaxLength) throw new Error(`Please keep this under ${noteMaxLength} characters.`);
  return trimmed;
}

function reviewText(text) {
  const trimmed = String(text || '').trim();
  if (trimmed.length < reviewMinLength || trimmed.length > reviewMaxLength) throw new Error(`Write between ${reviewMinLength} and ${reviewMaxLength} characters.`);
  return trimmed;
}

export const getWorkCompletion = (client, workId) => call(client, 'get_work_completion', { p_work_id: workId });
export const startWork = (client, workId) => call(client, 'start_work', { p_work_id: workId });
export async function submitCompletion(client, workId, note, attachmentIds = []) {
  const trimmed = String(note || '').trim();
  if (trimmed.length > noteMaxLength) throw new Error(`Please keep this under ${noteMaxLength} characters.`);
  if (attachmentIds.length > maxEvidenceFiles) throw new Error(`Add at most ${maxEvidenceFiles} files.`);
  return call(client, 'submit_completion', { p_work_id: workId, p_note: trimmed || null, p_attachment_ids: attachmentIds });
}
export const acceptCompletion = (client, workId) => call(client, 'accept_completion', { p_work_id: workId });
export const requestFollowUp = async (client, workId, text) => call(client, 'request_follow_up', { p_work_id: workId, p_reason: reason(text) });
export const raiseWorkProblem = async (client, workId, text) => call(client, 'raise_work_problem', { p_work_id: workId, p_reason: reason(text) });
export const cancelWork = async (client, workId, text) => call(client, 'cancel_work', { p_work_id: workId, p_reason: reason(text) });
export const markVisitCompleted = (client, visitId) => call(client, 'mark_visit_completed', { p_visit_id: visitId });
export async function submitReview(client, workId, rating, body) {
  if (!Number.isInteger(rating) || rating < 1 || rating > 5) throw new Error('Choose a rating from 1 to 5 stars.');
  return call(client, 'submit_review', { p_work_id: workId, p_rating: rating, p_body: reviewText(body) });
}
export const respondToReview = async (client, reviewId, body) => call(client, 'respond_to_review', { p_review_id: reviewId, p_body: reviewText(body) });
export const listCompanyReviews = (client, organizationId, limit = 20, offset = 0) =>
  call(client, 'list_company_reviews', { p_organization_id: organizationId, p_limit: limit, p_offset: offset });
